import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import css from './LandingPage.module.css';
import config from '../../config/config';
/* Still needed by the JSON-LD below (primaryImageOfPage / image), even though
   the og: and twitter: image tags now come from Page's own default. */
import facebookImage from '../../assets/og-image.png';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import {
  servicePageIds,
  resolveServicePage,
} from '../../config/configServicePages';
import { t } from '../../util/messages';
import {
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  Page,
  Footer,
} from '../../components';
import SectionHero from './SectionHero/SectionHero';
import SectionPortfolio from './SectionPortfolio/SectionPortfolio';
import SectionServices from './SectionServices/SectionServices';
import SectionStatsAndProcess from './SectionStatsAndProcess/SectionStatsAndProcess';
import { TopbarContainer } from '../../containers';
import SectionClientReviews from './SectionClientReviews/SectionClientReviews';
import SectionWhyUs from './SectionWhyUs/SectionWhyUs';
import SectionCta from './SectionCta/SectionCta';
import SectionContactUs from './SectionContactUs/SectionContactUs';

const LandingPage = ({ history, location }) => {
  const [scrollingDisabled, setScrollingDisabled] = useState(false);

  // One observer drives the staggered entrance for every section below.
  useScrollReveal();

  const onManageDisableScrolling = (_componentId, disabled = false) => {
    setScrollingDisabled(disabled);
  };

  const siteTitle = config.siteTitle;
  const schemaTitle = t('LandingPage.schemaTitle', { siteTitle });
  const schemaDescription = t('LandingPage.schemaDescription');

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={schemaDescription}
      title={schemaTitle}
      /* Share images come from Page's default, which uses this same asset at
         its real dimensions. The sizes declared here were 1200x630 and
         600x314 for a file that is 851x385. */
      /*
       * Linked by @id to the WebSite and Organization nodes Page.js emits,
       * so the three resolve as one graph rather than three loose objects.
       */
      schema={[
        /*
         * The services the page links to, as a list a crawler can read. Names
         * and URLs come from the same registry that builds the routes, so the
         * list cannot name a page that does not exist. Reviews are deliberately
         * not marked up: Google excludes self-served reviews of the publisher's
         * own business from rich results, and marking them up invites a manual
         * action for no gain.
         */
        {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          '@id': `${config.canonicalRootURL}/#services`,
          name: 'Services',
          itemListElement: servicePageIds.map((id, index) => {
            const service = resolveServicePage(id);
            return {
              '@type': 'ListItem',
              position: index + 1,
              name: service.title,
              url: `${config.canonicalRootURL}/services/${service.slug}`,
            };
          }),
        },
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          '@id': `${config.canonicalRootURL}/#webpage`,
          url: `${config.canonicalRootURL}/`,
          description: schemaDescription,
          name: schemaTitle,
          inLanguage: 'en',
          isPartOf: { '@id': `${config.canonicalRootURL}/#website` },
          about: { '@id': `${config.canonicalRootURL}/#organization` },
          primaryImageOfPage: `${config.canonicalRootURL}${facebookImage}`,
          image: [`${config.canonicalRootURL}${facebookImage}`],
        },
      ]}
    >
      <Helmet>
        <script
          src={`https://www.google.com/recaptcha/enterprise.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`}
          async
          defer
        />
      </Helmet>
      <LayoutSingleColumn className={css.root}>
        <LayoutWrapperTopbar>
          <TopbarContainer
            onManageDisableScrolling={onManageDisableScrolling}
          />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <div className={css.wrapper}>
            <div className={css.container}>
              <div className={css.heroContainer}>
                <SectionHero history={history} location={location} />
              </div>
              <div className={css.portfolioContainer}>
                <SectionPortfolio history={history} location={location} />
              </div>
              <div className={css.servicesContainer}>
                <SectionServices history={history} location={location} />
              </div>
              <div className={css.statsAndProcessContainer}>
                <SectionStatsAndProcess history={history} location={location} />
              </div>
              <div className={css.whyUsContainer}>
                <SectionWhyUs history={history} location={location} />
              </div>
              <div className={css.clientReviewsContainer}>
                <SectionClientReviews history={history} location={location} />
              </div>
              <div className={css.ctaContainer}>
                <SectionCta history={history} location={location} />
              </div>
              <div className={css.contactUsContainer}>
                <SectionContactUs history={history} location={location} />
              </div>
            </div>
            <div className={css.footerContainer}>
              <Footer />
            </div>
          </div>
        </LayoutWrapperMain>
      </LayoutSingleColumn>
    </Page>
  );
};

export default LandingPage;
